// https://developers.google.com/cast/docs/web_sender/integrate

import { Context } from '@nuxt/types'
import { Store } from 'vuex'
import {
  Chrome,
  ChromecastFramework,
  ChromecastStatus,
  MediaInfo,
  RemotePlayer,
  RemotePlayerController,
  SessionStateEventData,
} from '~/models/chromecast'
import { Sermon } from '~/models/sermon'
import { Webcast } from '~/models/webcast'
import { PlayerCastPlatform, PlayerMediaType } from '~/assets/ts/enums'

declare global {
  interface Window {
    cast?: ChromecastFramework
    chrome?: Chrome
    __onGCastApiAvailable?: (available: boolean) => void
  }
}

export default ({ store }: Context) => {
  if (typeof window === 'undefined') return

  const existingScript = document.querySelector('script[src*="cast_sender.js"]')
  if (existingScript) return

  const script = document.createElement('script')
  script.src =
    'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1'
  script.async = true

  function CanChromecast(canChromecast: boolean) {
    store.commit('player/SET_PLAYER_CAN_CHROMECAST', canChromecast)
  }

  script.onload = () => {
    console.log('[Chromecast] SDK loaded')

    // Wait for cast framework to become available
    window.__onGCastApiAvailable = (isAvailable) => {
      if (!window.cast || !window.chrome) {
        CanChromecast(false)
        return
      }
      if (isAvailable) {
        instance()?.setOptions({
          receiverApplicationId: '3524DCDC',
          autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        })

        CanChromecast(true)

        store.watch(
          (_, getters) => getters['player/canCast'],
          (canCast: boolean) => {
            if (canCast) {
              AddChromecastEvents(store)
            } else {
              RemoveChromecastEvents(store)
            }
          },
          { immediate: true }
        )

        store.watch(
          (_, getters) => getters['player/sermon'],
          (sermon?: Sermon) => {
            if (sermon && session()) {
              ResumeSession(store)
            }
          },
          { immediate: true }
        )

        store.watch(
          (_, getters) => getters['player/webcast'],
          (webcast?: Webcast) => {
            if (webcast && session()) {
              ResumeSession(store)
            }
          },
          { immediate: true }
        )
      } else {
        console.warn('[Chromecast] Cast API is not available')
        CanChromecast(false)
      }
    }
  }

  script.onerror = () => {
    console.error('[Chromecast] Failed to load Cast SDK')
    CanChromecast(false)
  }

  document.head.appendChild(script)
}

let listenersAdded = false
let player: RemotePlayer | null = null
let controller: RemotePlayerController | null = null
let sessionListener: ((event: SessionStateEventData) => void) | null = null
const playerUnwatchers: Array<() => void> = []

function AddChromecastEvents(store: Store<any>) {
  if (listenersAdded || !window.cast) return
  const framework = window.cast.framework
  if (!framework) return

  sessionListener = (event: SessionStateEventData) => {
    const { SESSION_STARTED, SESSION_RESUMED, SESSION_ENDED } =
      framework.SessionState
    switch (event.sessionState) {
      case SESSION_STARTED:
        StartSession(store)
        break
      case SESSION_RESUMED:
        ResumeSession(store)
        break
      case SESSION_ENDED:
        EndSession(store)
        break
    }
  }

  instance()?.addEventListener(
    framework.CastContextEventType.SESSION_STATE_CHANGED,
    sessionListener
  )

  listenersAdded = true
  console.log('[Chromecast] Session Events Added')
}

function RemoveChromecastEvents(store: Store<any>) {
  if (!listenersAdded || !window.cast) return

  if (sessionListener) {
    instance()?.removeEventListener(
      window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      sessionListener
    )
    sessionListener = null
  }

  RemovePlayerEvents(store)
  listenersAdded = false
  console.log('[Chromecast] Session Events Removed')
}

function session() {
  return instance()?.getCurrentSession() || undefined
}

function instance() {
  return window.cast?.framework.CastContext.getInstance() || undefined
}

function StartSession(store: Store<any>) {
  const chrome = window.chrome
  if (!chrome) return

  const metadata = getMetadata(store, chrome)
  if (!metadata) return

  console.log('[Chromecast] Session Starting')
  const load = new chrome.cast.media.LoadRequest(metadata)
  load.currentTime = store.getters['player/playerTime']
  // updating this is tricky, so lets just keep it to 1
  // load.playbackRate = store.getters['player/speed']

  session()
    ?.loadMedia(load)
    .then(() => {
      console.log('[Chromecast] Session Started Successfully')
      AddPlayerEvents(store)
    })
    .catch((err) => {
      console.error('[Chromecast] Failed to start session', err)
    })
}

function ResumeSession(store: Store<any>) {
  if (!window.chrome) return
  const sessionId = session()?.getMediaSession()?.media.contentId
  const pageId = getMetadata(store, window.chrome)?.contentId
  if (pageId === sessionId) {
    console.log('[Chromecast] Session Resuming')
    if (player) {
      SetCurrentTime(store, player.currentTime)
    }
    AddPlayerEvents(store)
  } else {
    StartSession(store)
  }
}

function EndSession(store: Store<any>) {
  const s = session()
  if (!s) return
  console.log('[Chromecast] Session Ended')
  s.endSession(true)
  SetStatus(store, ChromecastStatus.Disconnected)
  RemovePlayerEvents(store)
  store.commit('player/SET_PLAYER_CASTING', PlayerCastPlatform.None)
}

function SetVolume(
  store: Store<any>,
  player: RemotePlayer,
  controller: RemotePlayerController
) {
  player.volumeLevel = store.getters['player/volume'] / 100
  controller.setVolumeLevel()
}

function SetMuted(
  store: Store<any>,
  player: RemotePlayer,
  controller: RemotePlayerController
) {
  if (player.isMuted === store.getters['player/muted']) return
  controller.muteOrUnmute()
}

function SetCurrentTime(store: Store<any>, time: number) {
  store.commit('player/SET_CHROMECAST_TIME', time)
}

function SetStatus(store: Store<any>, status: ChromecastStatus) {
  store.commit('player/SET_PLAYER_CHROMECAST_STATUS', status)
}

function SetPaused(store: Store<any>, paused: boolean) {
  SetStatus(store, paused ? ChromecastStatus.Paused : ChromecastStatus.Playing)
}

const playerEventHandlers = {
  CURRENT_TIME_CHANGED: (store: Store<any>, e: any) => {
    SetCurrentTime(store, e.value)
  },
  VOLUME_LEVEL_CHANGED: (store: Store<any>, e: any) => {
    store.commit('player/SET_PLAYER_VOLUME', e.value * 100)
  },
  IS_MUTED_CHANGED: (store: Store<any>, e: any) => {
    store.commit('player/SET_PLAYER_MUTED', e.value)
  },
  IS_PAUSED_CHANGED: (store: Store<any>, e: any) => {
    SetPaused(store, e.value)
  },
}

function AddPlayerEvents(store: Store<any>) {
  if (!window.cast) return
  store.commit('player/SET_PLAYER_CASTING', PlayerCastPlatform.Chromecast)

  player = new window.cast.framework.RemotePlayer()
  controller = new window.cast.framework.RemotePlayerController(player)

  SetVolume(store, player, controller)
  SetMuted(store, player, controller)
  SetPaused(store, player.isPaused)
  SetCurrentTime(store, player.currentTime)

  const events = window.cast.framework.RemotePlayerEventType
  controller.addEventListener(events.CURRENT_TIME_CHANGED, (e) =>
    playerEventHandlers.CURRENT_TIME_CHANGED(store, e)
  )
  controller.addEventListener(events.VOLUME_LEVEL_CHANGED, (e) =>
    playerEventHandlers.VOLUME_LEVEL_CHANGED(store, e)
  )
  controller.addEventListener(events.IS_MUTED_CHANGED, (e) =>
    playerEventHandlers.IS_MUTED_CHANGED(store, e)
  )
  controller.addEventListener(events.IS_PAUSED_CHANGED, (e) =>
    playerEventHandlers.IS_PAUSED_CHANGED(store, e)
  )

  // Watch Vuex state changes and update Chromecast player
  playerUnwatchers.push(
    store.watch(
      (_, getters) => getters['player/volume'],
      (_: number) => {
        if (player && controller) {
          SetVolume(store, player, controller)
        }
      }
    )
  )

  playerUnwatchers.push(
    store.watch(
      (_, getters) => getters['player/muted'],
      (_: boolean) => {
        if (player && controller) {
          SetMuted(store, player, controller)
        }
      }
    )
  )

  playerUnwatchers.push(
    store.watch(
      (_, getters) => getters['player/playerTime'],
      (newTime: number) => {
        if (!player || !controller) return
        if (Math.abs(newTime - player.currentTime) < 2) return
        player.currentTime = newTime
        controller.seek()
      }
    )
  )

  playerUnwatchers.push(
    store.watch(
      (_, getters) => getters['player/chromecastStatus'],
      (status: ChromecastStatus) => {
        if (!controller || !player) return
        const paused = player.isPaused
        if (
          (status === ChromecastStatus.Playing && paused) ||
          (status === ChromecastStatus.Paused && !paused)
        ) {
          SetPaused(store, !paused)
          controller.playOrPause()
        }
      }
    )
  )

  playerUnwatchers.push(
    store.watch(
      (_, getters) => getters['player/mediaType'],
      (_: PlayerMediaType) => {
        if (store.getters['player/webcast']) return
        StartSession(store)
      }
    )
  )

  console.log('[Chromecast] Player Events Added')
}

function RemovePlayerEvents(store: Store<any>) {
  store.commit('player/SET_PLAYER_CASTING', PlayerCastPlatform.None)
  if (!controller || !window.cast) return

  const events = window.cast.framework.RemotePlayerEventType
  controller.removeEventListener(events.CURRENT_TIME_CHANGED)
  controller.removeEventListener(events.VOLUME_LEVEL_CHANGED)
  controller.removeEventListener(events.IS_MUTED_CHANGED)
  controller.removeEventListener(events.IS_PAUSED_CHANGED)

  playerUnwatchers.forEach((unwatch) => unwatch())
  playerUnwatchers.length = 0

  controller = null
  player = null

  console.log('[Chromecast] Player Events Removed')
}

function getMetadata(store: Store<any>, chrome: Chrome) {
  const audio = store.getters['player/mediaType'] === PlayerMediaType.Audio
  const sermon = store.getters['player/sermon'] as Sermon | undefined
  if (sermon) {
    return getSermonMetadata(chrome, sermon, audio)
  }
  const webcast = store.getters['player/webcast'] as Webcast | undefined
  if (webcast && !webcast.AudioOnly) {
    return getWebcastMetadata(chrome, webcast)
  }
}

function getSermonMetadata(
  chrome: Chrome,
  sermon: Sermon,
  audio: boolean
): MediaInfo | undefined {
  const m = sermon.media
  const media = audio ? m?.highestAudio : m?.videoAdaptive
  if (!media?.streamURL) return

  const mediaInfo = new chrome.cast.media.MediaInfo(
    media.streamURL,
    audio ? 'audio' : 'video'
  )
  const metadata = new chrome.cast.media.GenericMediaMetadata()
  metadata.title = sermon.fullTitle
  metadata.subtitle = sermon.metadata
  metadata.images = [new chrome.cast.Image(sermon.chromecastArt(audio))]
  mediaInfo.metadata = metadata

  return mediaInfo
}

function getWebcastMetadata(
  chrome: Chrome,
  webcast: Webcast
): MediaInfo | undefined {
  const mediaInfo = new chrome.cast.media.MediaInfo(
    webcast.eventStreamUrl,
    'video'
  )
  const metadata = new chrome.cast.media.GenericMediaMetadata()
  const displayName = webcast.broadcaster.displayName
  metadata.title = webcast.title ?? displayName
  metadata.subtitle = webcast.title ? displayName : ''
  metadata.images = [new chrome.cast.Image(webcast.chromecastArt(false))]
  mediaInfo.metadata = metadata
  mediaInfo.streamType = 'LIVE'

  return mediaInfo
}
